import { useState, useEffect } from 'react';
import { useLoginWithSms, usePrivy } from '@privy-io/react-auth';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, Phone, Loader2 } from 'lucide-react';

export function LoginForm() {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [code, setCode] = useState('');
  const [currentStep, setCurrentStep] = useState<'phone' | 'code'>('phone');
  const { state, sendCode, loginWithCode } = useLoginWithSms();
  const { authenticated } = usePrivy();

  // Reset form when not authenticated (after logout)
  useEffect(() => {
    if (!authenticated) {
      setPhoneNumber('');
      setCode('');
      setCurrentStep('phone');
    }
  }, [authenticated]);

  const handleSendCode = async () => {
    if (!phoneNumber.trim()) return;

    try {
      await sendCode({ phoneNumber: phoneNumber.trim() });
      // Move to code step regardless of Privy state
      setCurrentStep('code');
    } catch (error) {
      console.error('Failed to send code:', error);
    }
  };

  const handleVerifyCode = async () => {
    if (!code.trim()) return;

    try {
      await loginWithCode({ code: code.trim() });
    } catch (error) {
      console.error('Failed to verify code:', error);
    }
  };

  // Use our own step state instead of relying on Privy's state
  const isPhoneStep = currentStep === 'phone';
  const isCodeStep = currentStep === 'code';
  const hasError = state.status === 'error';
  const isLoading =
    state.status === 'sending-code' || state.status === 'submitting-code';

  // Show loading screen only when authenticated and login was successful
  const showAuthenticating = state.status === 'done' && authenticated;
  
  if (showAuthenticating) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Logging you in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-primary/10 rounded-full mb-4">
            <Phone className="w-6 h-6 text-primary" />
          </div>
          <CardTitle className="text-center">
            {isPhoneStep ? 'Sign in with SMS' : 'Enter verification code'}
          </CardTitle>
          <CardDescription className="text-center">
            {isPhoneStep
              ? 'Enter your phone number to receive a verification code'
              : `We sent a code to ${phoneNumber}`}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {hasError && (
            <div 
              className="flex items-center space-x-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md"
              data-testid="auth-error"
            >
              <AlertCircle className="w-4 h-4 text-destructive" />
              <span className="text-sm text-destructive" data-testid="auth-error-message">
                Authentication failed. Please try again.
              </span>
            </div>
          )}

          {isPhoneStep && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  disabled={isLoading}
                  className="h-11"
                  data-testid="phone-input"
                />
                <p className="text-xs text-muted-foreground">
                  Include country code (e.g., +1 for US)
                </p>
              </div>

              <Button
                onClick={handleSendCode}
                disabled={!phoneNumber.trim() || isLoading}
                className="w-full h-11"
                data-testid="send-otp-btn"
              >
                {isLoading ? 'Sending...' : 'Send Code'}
              </Button>
            </div>
          )}

          {isCodeStep && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">Verification Code</Label>
                <Input
                  id="code"
                  type="text"
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  disabled={isLoading}
                  className="h-11 text-center text-lg tracking-widest"
                  maxLength={6}
                  data-testid="otp-input"
                />
              </div>

              <div className="space-y-3">
                <Button
                  onClick={handleVerifyCode}
                  disabled={!code.trim() || isLoading}
                  className="w-full h-11"
                  data-testid="verify-otp-btn"
                >
                  {isLoading ? 'Verifying...' : 'Verify Code'}
                </Button>

                <Button
                  variant="ghost"
                  onClick={() => {
                    setCode('');
                    setPhoneNumber('');
                    // Go back to phone step
                    setCurrentStep('phone');
                  }}
                  disabled={isLoading}
                  className="w-full h-11"
                  data-testid="use-different-number-btn"
                >
                  Use different number
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
